/*
  DASH section library — Live reviews (sections/lp-testimonials.liquid)
  Fetches approved testimonials from the app through the Shopify app proxy and
  renders them as a wall with a one-row toolbar: a result count, a dropdown per
  filter dimension, the sort control and a clear-all. Every dropdown's options
  and counts come from the feed's facets, so a control can never offer a value
  that returns nothing.

  One instance per [data-lp-testimonials]; everything it needs is on that root's
  data-* attributes. State also lives in the URL (?sort=&proof=&priority=&stage=
  and ?topic=) so a filtered view can be shared and the back button behaves.

  Topic pills (Trello 380): when the section has Topic blocks, a row of toggle
  buttons narrows the feed by one dog need, proof type or set of outcome tags.
  Exactly one pill is pressed at a time; the block marked default is pressed on
  arrival and is left out of the URL.

  Search + chips (2026-10-14): an optional search field sends `q` to the feed
  (text, pull quote and breed), searching as the visitor pauses typing, and the
  matched words are highlighted in the cards. A status line under the toolbar
  shows one removable chip per search or dropdown choice. A Dog need pill and
  the Dog need dropdown filter the same field, so choosing one resets the other.

  Two things the markup does NOT contain and this file must not invent: a star
  filter and a rating histogram. 98% of the approved library is five-star, so
  the control would be dead, and a distribution reads as an aggregate rating —
  which for this library is selection-biased. See docs/verified-claims.md.

  Safety: review text, names and labels are set with textContent. The only
  markup built by hand is the star row, from an integer.
*/
(function () {
  'use strict';

  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var VERIFIED_SOURCES = { stamped: 'Verified purchase', shop_review: 'Verified purchase', trustpilot: 'Trustpilot review', retention_survey: 'Customer survey' };
  // Facet key -> the query parameter the app expects, which is also the URL key here.
  var GROUP_KEYS = ['proof', 'priority', 'stage'];
  // Same bounds as the app: shorter matches nearly everything, longer is not a search.
  var MIN_SEARCH = 2;
  var MAX_SEARCH = 60;
  var SEARCH_DELAY = 350;

  /* Last resort for a label the feed has never sent — someone landing straight on
     a shared link whose filter matches nothing. "SENSITIVE_TUMMY" -> "Sensitive
     tummy", "digestive" -> "Digestive". */
  function prettify(value) {
    return String(value).replace(/[_-]+/g, ' ').toLowerCase().replace(/^./, function (c) { return c.toUpperCase(); });
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  }

  function escapeRegExp(text) {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /* Wraps every case-insensitive occurrence of the search phrase in <mark>,
     working on text nodes so review text is never parsed as HTML. */
  function highlight(node, term) {
    if (!term) return;
    var re = new RegExp(escapeRegExp(term), 'gi');
    var walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT, null);
    var texts = [];
    while (walker.nextNode()) texts.push(walker.currentNode);
    texts.forEach(function (t) {
      var value = t.nodeValue;
      re.lastIndex = 0;
      if (!re.test(value)) return;
      re.lastIndex = 0;
      var frag = document.createDocumentFragment();
      var last = 0;
      var m;
      while ((m = re.exec(value)) !== null) {
        if (m.index > last) frag.appendChild(document.createTextNode(value.slice(last, m.index)));
        frag.appendChild(el('mark', 'tw-mark', m[0]));
        last = m.index + m[0].length;
      }
      if (last < value.length) frag.appendChild(document.createTextNode(value.slice(last)));
      t.parentNode.replaceChild(frag, t);
    });
  }

  function stars(count) {
    var n = Math.max(0, Math.min(5, parseInt(count, 10) || 0));
    var wrap = el('span', 'tw-stars');
    wrap.setAttribute('role', 'img');
    wrap.setAttribute('aria-label', n + ' out of 5 stars');
    for (var i = 1; i <= 5; i++) {
      var s = el('span', i <= n ? 'tw-star' : 'tw-star tw-star--off', '★');
      s.setAttribute('aria-hidden', 'true');
      wrap.appendChild(s);
    }
    return wrap;
  }

  function formatDate(iso) {
    var m = /^(\d{4})-(\d{2})/.exec(iso || '');
    if (!m) return '';
    return MONTHS[parseInt(m[2], 10) - 1] + ' ' + m[1];
  }

  function card(item, term) {
    var li = el('li', 'tw-card');
    li.setAttribute('data-ref', item.ref || '');

    var top = el('div', 'tw-card__top');
    top.appendChild(stars(item.stars));
    var pill = (item.proofLabel || '').replace(/ Proof$/, '');
    if (pill) top.appendChild(el('span', 'tw-card__pill', pill));
    li.appendChild(top);

    var quote = (item.quote || '').trim();
    var text = (item.text || '').trim();
    // The long element gets the clamp class; whether it actually clamps is
    // decided by measurement after layout (readMore below), not by a character
    // count, so a card only grows a "Read more" when it really is cut off.
    if (quote && text && quote !== text && text.length > quote.length) {
      li.appendChild(el('p', 'tw-card__quote', '“' + quote.replace(/^["“]|["”.]+$/g, '') + '”'));
      li.appendChild(el('p', 'tw-card__text tw-clamp', text));
    } else {
      li.appendChild(el('blockquote', 'tw-card__quote tw-clamp', '“' + (text || quote) + '”'));
    }

    var meta = el('div', 'tw-card__meta');
    meta.appendChild(el('cite', 'tw-card__name', item.name || 'DASH customer'));
    var date = formatDate(item.date);
    if (date) meta.appendChild(el('span', 'tw-card__date', date));
    var verified = VERIFIED_SOURCES[item.source];
    if (verified) {
      var v = el('span', 'tw-card__verified');
      var icon = el('i', 'fas fa-circle-check');
      icon.setAttribute('aria-hidden', 'true');
      v.appendChild(icon);
      v.appendChild(document.createTextNode(verified));
      meta.appendChild(v);
    }
    li.appendChild(meta);
    if (term) {
      li.querySelectorAll('.tw-card__quote, .tw-card__text').forEach(function (n) { highlight(n, term); });
    }
    return li;
  }

  /* Long reviews are line-clamped in CSS and get a Read more toggle — but only
     the ones that are genuinely cut off. Measuring after a frame is the only
     honest test: card width, font loading and the quote/body split all change
     where the text lands, so a character threshold would put a dead "Read more"
     under short reviews and miss long ones in a narrow column. */
  function readMore(scope, labelMore, labelLess) {
    function pass() {
      /* A node is marked only once a button is attached, so a node that did not
         overflow on an early pass is measured again later. That matters: the
         first frame can run before the web font swaps in, and the fallback font
         fits text that DM Sans clamps, which left every long review without its
         toggle. */
      scope.querySelectorAll('.tw-clamp:not([data-tw-clamped])').forEach(function (node) {
        if (node.scrollHeight <= node.clientHeight + 2) return;
        var card = node.closest('.tw-card');
        if (!card) return;
        node.setAttribute('data-tw-clamped', '1');
        var btn = el('button', 'tw-card__more', labelMore);
        btn.type = 'button';
        btn.setAttribute('aria-expanded', 'false');
        btn.addEventListener('click', function () {
          var open = card.classList.toggle('is-expanded');
          btn.textContent = open ? labelLess : labelMore;
          btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
        node.insertAdjacentElement('afterend', btn);
        // A search match cut off by the clamp is no use to the visitor who searched
        // for it: open that card so the highlighted word is on screen.
        var mark = node.querySelector('.tw-mark');
        if (mark && mark.getBoundingClientRect().bottom > node.getBoundingClientRect().bottom + 1) btn.click();
      });
    }
    window.requestAnimationFrame(function () { window.requestAnimationFrame(pass); });
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(pass).catch(function () {});
    }
  }

  // The library holds the same review more than once when a customer reviewed two
  // products with identical text; show each wording once per page view.
  function dedupe(items, seen) {
    return items.filter(function (item) {
      var key = ((item.text || item.quote || '') + '|' + (item.name || '')).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
      if (!key || seen[key]) return false;
      seen[key] = true;
      return true;
    });
  }

  // Keep one reviewer's several reviews from sitting side by side: pull a card
  // forward when the previous card carries the same name. Order otherwise kept.
  function spread(items) {
    var out = [];
    var pool = items.slice();
    while (pool.length) {
      var idx = 0;
      if (out.length) {
        var prev = (out[out.length - 1].name || '').toLowerCase();
        for (var i = 0; i < pool.length; i++) {
          if ((pool[i].name || '').toLowerCase() !== prev) { idx = i; break; }
        }
      }
      out.push(pool.splice(idx, 1)[0]);
    }
    return out;
  }

  function init(root) {
    var d = root.dataset;
    var list = root.querySelector('[data-tw-list]');

    var toolbar = root.querySelector('[data-tw-toolbar]');
    var sortSelect = root.querySelector('[data-tw-sort]');
    var countLine = root.querySelector('[data-tw-count]');
    // Two of these: one in the toolbar, one in the empty state. Both must work —
    // when a filter combination returns nothing, the empty state's button is the
    // visitor's only way back, and querySelector would have bound the toolbar's.
    var clearBtns = root.querySelectorAll('[data-tw-clear]');
    var toolbarClear = root.querySelector('.tw__clear');

    var searchForm = root.querySelector('[data-tw-search-form]');
    var searchInput = root.querySelector('[data-tw-search]');
    var searchClear = root.querySelector('[data-tw-search-clear]');
    var chipList = root.querySelector('[data-tw-chips]');
    var emptyText = root.querySelector('[data-tw-empty-text]');
    var emptyDefault = emptyText ? emptyText.textContent : '';
    var searchTimer = null;

    var summary = root.querySelector('[data-tw-summary]');
    var more = root.querySelector('[data-tw-more]');
    var empty = root.querySelector('[data-tw-empty]');
    var error = root.querySelector('[data-tw-error]');
    var perPage = parseInt(d.perPage, 10) || 12;
    /* A phone shows one card per row, so the desktop batch becomes a screen and
       a half of uninterrupted reviews before any other content (Trello 380
       spec §9). Same setting, capped for the narrow layout — read once, so a
       rotation mid-session does not change the page size under the visitor. */
    if (window.matchMedia && window.matchMedia('(max-width: 749px)').matches) {
      perPage = Math.min(perPage, 6);
    }

    // A dimension pinned in the theme editor is a hard scope for the whole section,
    // so its filter group is never offered — otherwise a visitor could click their
    // way out of the scope the page was built around.
    var pinned = { proof: d.proof || '', stage: d.stage || '', priority: '' };
    var groups = [
      { key: 'proof', label: d.labelTopic || 'Topic', on: d.filterTopic === 'true' && !pinned.proof },
      { key: 'priority', label: d.labelNeed || 'Dog need', on: d.filterNeed === 'true' && !pinned.priority },
      { key: 'stage', label: d.labelStage || 'Life stage', on: d.filterStage === 'true' && !pinned.stage },
    ].filter(function (g) {
      g.select = root.querySelector('[data-tw-facet="' + g.key + '"]');
      g.field = root.querySelector('[data-tw-field="' + g.key + '"]');
      return g.on && g.select;
    });

    var topics = Array.prototype.slice.call(root.querySelectorAll('[data-tw-topic]'));
    var defaultTopic = topics.filter(function (t) { return t.hasAttribute('data-default'); })[0] || topics[0] || null;
    var defaultTopicKey = defaultTopic ? defaultTopic.getAttribute('data-tw-topic') : '';

    var state = { sort: d.sort || 'score', selected: { proof: '', priority: '', stage: '' }, q: '', topic: defaultTopicKey, offset: 0, loading: false, queued: false, seen: {}, sortsBuilt: false, labels: {} };

    function topicButton(key) {
      return topics.filter(function (t) { return t.getAttribute('data-tw-topic') === key; })[0] || null;
    }

    function topicNarrows() {
      var t = topicButton(state.topic);
      return !!(t && t.getAttribute('data-match') !== 'all' && t.getAttribute('data-value'));
    }

    function paintTopics() {
      topics.forEach(function (t) {
        t.setAttribute('aria-pressed', t.getAttribute('data-tw-topic') === state.topic ? 'true' : 'false');
      });
    }

    /* The field a pill narrows ('priority', 'proof') or '' for tags / all. A pill
       and a dropdown on the same field cannot both apply, and the request sends
       the pill last, so without this the dropdown would silently do nothing. */
    function topicField(key) {
      var t = topicButton(key);
      if (!t || !t.getAttribute('data-value')) return '';
      var match = t.getAttribute('data-match');
      return match === 'priority' || match === 'proof' ? match : '';
    }

    function normaliseSearch(value) {
      var v = String(value || '').replace(/\s+/g, ' ').trim().slice(0, MAX_SEARCH);
      return v.length >= MIN_SEARCH ? v : '';
    }

    function activeGroupKeys() {
      return groups.map(function (g) { return g.key; });
    }

    function isFiltered() {
      return topicNarrows() || !!state.q || activeGroupKeys().some(function (k) { return !!state.selected[k]; });
    }

    function readUrl() {
      var q = new URLSearchParams(window.location.search);
      activeGroupKeys().forEach(function (k) {
        var v = q.get(k);
        if (v) state.selected[k] = v;
      });
      var sort = q.get('sort');
      if (sort) state.sort = sort;
      var topic = q.get('topic');
      if (topic && topicButton(topic)) state.topic = topic;
      if (searchInput) state.q = normaliseSearch(q.get('q'));
      // A shared link carrying both a Dog need pill and a Dog need choice: the
      // dropdown is the more specific, so it wins and the pill falls back.
      var field = topicField(state.topic);
      if (field && state.selected[field]) state.topic = defaultTopicKey;
    }

    /* Only this section's own keys are touched, so a campaign's utm_* or an
       experiment parameter on the URL survives a filter click. */
    function writeUrl() {
      if (!window.history || !window.history.replaceState) return;
      var q = new URLSearchParams(window.location.search);
      activeGroupKeys().forEach(function (k) {
        if (state.selected[k]) q.set(k, state.selected[k]); else q.delete(k);
      });
      if (state.sort && state.sort !== (d.sort || 'score')) q.set('sort', state.sort); else q.delete('sort');
      if (topics.length && state.topic && state.topic !== defaultTopicKey) q.set('topic', state.topic); else q.delete('topic');
      if (searchInput && state.q) q.set('q', state.q); else q.delete('q');
      var search = q.toString();
      window.history.replaceState({ tw: true }, '', window.location.pathname + (search ? '?' + search : '') + window.location.hash);
    }

    function request(offset) {
      var p = new URLSearchParams();
      p.set('placement', d.placement || 'homepage');
      // Pinned scope first, then the visitor's choice for the groups on offer.
      if (pinned.proof) p.set('proof', pinned.proof);
      if (pinned.stage) p.set('stage', pinned.stage);
      activeGroupKeys().forEach(function (k) {
        if (state.selected[k]) p.set(k, state.selected[k]);
      });
      // The pressed pill last, so it wins over a dropdown on the same dimension.
      var t = topicButton(state.topic);
      var match = t ? t.getAttribute('data-match') : 'all';
      var value = t ? t.getAttribute('data-value') : '';
      if (value && match === 'priority') p.set('priority', value.toUpperCase());
      if (value && match === 'proof') p.set('proof', value.toLowerCase());
      if (value && match === 'tags') p.set('tags', value.toLowerCase().replace(/\s+/g, ''));
      if (state.q) p.set('q', state.q);
      if (d.minStars && d.minStars !== '0') p.set('min_stars', d.minStars);
      if (d.since) p.set('since', d.since);
      p.set('sort', state.sort);
      p.set('limit', String(perPage));
      p.set('offset', String(offset));

      return fetch(d.endpoint + '?' + p.toString(), { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); });
    }

    function renderSort(payload) {
      if (!sortSelect || state.sortsBuilt || !payload.sorts || payload.sorts.length < 2) return;
      sortSelect.textContent = '';
      payload.sorts.forEach(function (s) {
        var o = el('option', null, s.label);
        o.value = s.value;
        sortSelect.appendChild(o);
      });
      sortSelect.value = state.sort;
      sortSelect.disabled = false;
      state.sortsBuilt = true;
    }

    /* Rebuilt on every response, because the counts are conjunctive: choosing
       "Senior" changes what the topic counts mean. A value the current filter
       reduces to zero drops out of the payload, so a selected-but-absent value is
       re-added at zero — otherwise the option the visitor just chose would vanish
       from the control and leave them unable to undo it. */
    function renderFilters(payload) {
      if (!groups.length || !payload.facets) return;

      groups.forEach(function (group) {
        var options = (payload.facets[group.key] || []).slice();
        var selected = state.selected[group.key];

        // Remember every label the feed has shown for this dimension: a value the
        // current filter reduces to zero drops out of the payload, and its option
        // still has to read "Digestive", not the raw "digestive" from the URL.
        state.labels[group.key] = state.labels[group.key] || {};
        options.forEach(function (o) { state.labels[group.key][o.value] = o.label; });

        if (selected && !options.some(function (o) { return o.value === selected; })) {
          options.push({ value: selected, label: state.labels[group.key][selected] || prettify(selected), count: 0 });
        }

        // A dimension with nothing to choose between is not a filter. One value
        // means every review shares it, so the control would only ever be a no-op.
        var usable = options.length > 1 || (options.length === 1 && selected);
        if (group.field) group.field.hidden = !usable;
        if (!usable) { group.select.disabled = true; return; }

        group.select.textContent = '';
        group.select.appendChild(allOption(group));
        options.forEach(function (o) {
          var label = (o.label || '').replace(/ Proof$/, '');
          var opt = el('option', null, o.count ? label + ' (' + o.count + ')' : label);
          opt.value = o.value;
          group.select.appendChild(opt);
        });
        group.select.value = selected || '';
        group.select.disabled = false;
        group.select.classList.toggle('is-active', !!selected);
      });
    }

    /* The first option carries the dimension name, so a row of dropdowns reads
       "Topic / Dog need / Life stage" at rest and "Taste (217) / Dog need / ..."
       once something is chosen. That keeps the dimension legible without a
       separate label beside every control. */
    function allOption(group) {
      var opt = el('option', null, group.label);
      opt.value = '';
      return opt;
    }

    /* The library total is deliberately not shown unfiltered. An exact review
       count is banned copy on this page (Trello 380 spec: it changes over time
       and must not be load-bearing), and "270 reviews" printed above the wall
       is exactly that claim. Once a filter narrows the set the total describes
       the visitor's own selection rather than the store, and it is the number
       they need to judge whether to keep loading, so it comes back.
       The `show_total` section setting (off by default; on for the product
       page, Trello #492) shows "Showing 12 of N reviews" from the start. */
    function renderCount(payload) {
      if (!countLine) return;
      var shown = list.querySelectorAll('.tw-card').length;
      var total = payload.total || 0;
      var word = total === 1 ? 'review' : 'reviews';
      if (!isFiltered() && d.showTotal !== 'true') {
        countLine.textContent = 'Showing ' + shown + ' ' + word;
      } else {
        countLine.textContent = shown >= total
          ? total + ' ' + word
          : 'Showing ' + shown + ' of ' + total + ' ' + word;
      }
      if (toolbarClear) toolbarClear.hidden = !isFiltered();
    }

    /* One chip per thing the visitor typed or picked from a dropdown. The pressed
       pill is not repeated here: it is already visibly pressed one row up. */
    function renderChips() {
      if (!chipList) return;
      chipList.textContent = '';
      var chips = [];
      if (state.q) chips.push({ label: '“' + state.q + '”', clear: function () { setSearch(''); } });
      groups.forEach(function (g) {
        var v = state.selected[g.key];
        if (!v) return;
        var label = ((state.labels[g.key] || {})[v] || prettify(v)).replace(/ Proof$/, '');
        chips.push({ label: label, clear: function () { state.selected[g.key] = ''; g.select.value = ''; load(true); } });
      });
      chips.forEach(function (c) {
        var li = el('li', 'tw-chip');
        var btn = el('button', 'tw-chip__btn');
        btn.type = 'button';
        btn.setAttribute('aria-label', 'Remove filter: ' + c.label);
        btn.appendChild(el('span', 'tw-chip__label', c.label));
        var x = el('span', 'tw-chip__x', '×');
        x.setAttribute('aria-hidden', 'true');
        btn.appendChild(x);
        btn.addEventListener('click', function () { if (!state.loading) c.clear(); });
        li.appendChild(btn);
        chipList.appendChild(li);
      });
      chipList.hidden = chips.length === 0;
    }

    function renderEmptyText() {
      if (!emptyText) return;
      emptyText.textContent = state.q
        ? (d.emptySearch || 'No reviews mention “{q}” yet.').replace('{q}', state.q)
        : emptyDefault;
    }

    function syncSearchInput() {
      if (!searchInput) return;
      if (normaliseSearch(searchInput.value) !== state.q) searchInput.value = state.q;
      if (searchClear) searchClear.hidden = !searchInput.value;
    }

    /* Applies a search immediately. Typing goes through the debounce below; the
       form submit, the clear cross and the chip come straight here. */
    function setSearch(value) {
      window.clearTimeout(searchTimer);
      var next = normaliseSearch(value);
      if (searchInput && !value) searchInput.value = '';
      if (searchClear) searchClear.hidden = !(searchInput && searchInput.value);
      if (next === state.q) return;
      state.q = next;
      if (next) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'testimonials_search', search_term: next });
      }
      load(true);
    }

    function renderSummary(payload) {
      if (d.showSummary !== 'true' || !summary || !payload.summary || !payload.summary.rated) return;
      var s = payload.summary;
      var tpl = d.summaryTemplate || '{count} approved customer reviews';
      var text = tpl
        .replace('{average}', (Math.floor(s.average * 10) / 10).toFixed(1))
        .replace('{count}', String(s.rated))
        .replace('{five_star_percent}', String(s.fiveStarPercent));
      summary.textContent = '';
      // Stars only when the wording actually states the average. A count-only line
      // with a star row next to it reads as a rating the number never claimed.
      if (tpl.indexOf('{average}') !== -1) summary.appendChild(stars(Math.floor(s.average)));
      summary.appendChild(document.createTextNode(text));
      summary.hidden = false;
    }

    function load(reset) {
      // A search typed while a page is loading must not be lost: run it after.
      if (state.loading) { if (reset) state.queued = true; return; }
      state.loading = true;
      if (reset) state.offset = 0;
      list.setAttribute('aria-busy', 'true');
      if (more) more.setAttribute('aria-busy', 'true');
      error.hidden = true;
      empty.hidden = true;

      request(state.offset).then(function (payload) {
        if (reset) { list.textContent = ''; state.seen = {}; }
        list.querySelectorAll('.tw-card--skeleton').forEach(function (n) { n.remove(); });
        spread(dedupe(payload.items || [], state.seen)).forEach(function (item) { list.appendChild(card(item, state.q)); });
        state.offset += (payload.items || []).length;
        readMore(list, d.readMore || 'Read more', d.readLess || 'Read less');

        renderSort(payload);
        renderFilters(payload);
        renderSummary(payload);
        renderCount(payload);
        renderChips();
        renderEmptyText();
        syncSearchInput();
        if (toolbar) toolbar.hidden = false;
        writeUrl();

        empty.hidden = state.offset > 0;
        if (more) {
          more.hidden = !payload.hasMore;
          more.textContent = d.loadMore || 'Load more reviews';
        }
        root.dispatchEvent(new CustomEvent('lp-testimonials:loaded', { detail: { total: payload.total, shown: state.offset, sort: state.sort, filtered: isFiltered() } }));
      }).catch(function (err) {
        list.querySelectorAll('.tw-card--skeleton').forEach(function (n) { n.remove(); });
        if (state.offset === 0) error.hidden = false;
        if (window.console) console.warn('[lp-testimonials] feed unavailable:', err && err.message);
      }).then(function () {
        state.loading = false;
        list.setAttribute('aria-busy', 'false');
        if (more) more.removeAttribute('aria-busy');
        if (state.queued) { state.queued = false; load(true); }
      });
    }

    groups.forEach(function (group) {
      group.select.addEventListener('change', function () {
        if (state.loading) { group.select.value = state.selected[group.key] || ''; return; }
        state.selected[group.key] = group.select.value;
        if (group.select.value && topicField(state.topic) === group.key) {
          state.topic = defaultTopicKey;
          paintTopics();
        }
        load(true);
      });
    });

    topics.forEach(function (t) {
      t.addEventListener('click', function () {
        var key = t.getAttribute('data-tw-topic');
        if (state.loading || key === state.topic) return;
        state.topic = key;
        var field = topicField(key);
        if (field && state.selected[field]) {
          state.selected[field] = '';
          groups.forEach(function (g) { if (g.key === field) g.select.value = ''; });
        }
        paintTopics();
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'testimonials_topic_selected', topic: key });
        load(true);
      });
    });

    if (more) more.addEventListener('click', function () { load(false); });

    if (searchInput) {
      searchInput.addEventListener('input', function () {
        if (searchClear) searchClear.hidden = !searchInput.value;
        window.clearTimeout(searchTimer);
        var value = searchInput.value;
        // One character is not a search yet; deleting back to it is still a reset.
        if (value.trim().length > 0 && value.trim().length < MIN_SEARCH) return;
        searchTimer = window.setTimeout(function () { setSearch(value); }, SEARCH_DELAY);
      });
      if (searchForm) {
        searchForm.addEventListener('submit', function (e) {
          e.preventDefault();
          setSearch(searchInput.value);
        });
      }
      if (searchClear) {
        searchClear.addEventListener('click', function () {
          setSearch('');
          searchInput.focus();
        });
      }
    }

    if (sortSelect) {
      sortSelect.addEventListener('change', function () {
        if (state.loading) { sortSelect.value = state.sort; return; }
        state.sort = sortSelect.value;
        load(true);
      });
    }

    Array.prototype.forEach.call(clearBtns, function (btn) {
      btn.addEventListener('click', function () {
        if (state.loading) return;
        GROUP_KEYS.forEach(function (k) { state.selected[k] = ''; });
        groups.forEach(function (g) { g.select.value = ''; });
        state.topic = defaultTopicKey;
        state.q = '';
        window.clearTimeout(searchTimer);
        if (searchInput) searchInput.value = '';
        paintTopics();
        load(true);
      });
    });

    // Someone arriving on a shared link, or using the back button after a filter
    // click, gets the state the URL describes rather than the section's defaults.
    window.addEventListener('popstate', function () {
      GROUP_KEYS.forEach(function (k) { state.selected[k] = ''; });
      state.sort = d.sort || 'score';
      state.topic = defaultTopicKey;
      state.q = '';
      readUrl();
      paintTopics();
      if (sortSelect && state.sortsBuilt) sortSelect.value = state.sort;
      groups.forEach(function (g) { g.select.value = state.selected[g.key] || ''; });
      load(true);
    });

    readUrl();
    paintTopics();
    if (sortSelect) sortSelect.value = state.sort;
    syncSearchInput();
    load(true);
  }

  function boot() {
    document.querySelectorAll('[data-lp-testimonials]:not([data-tw-ready])').forEach(function (root) {
      root.setAttribute('data-tw-ready', '1');
      init(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  // Theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', boot);
})();
